import PageNavbar from '../../components/feature/PageNavbar';
import Footer from '../home/components/Footer';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

const sections = [
  { id: 'angaben', label: 'Angaben gem. § 5 TMG' },
  { id: 'kontakt', label: 'Kontakt' },
  { id: 'verantwortlich', label: 'Verantwortlich' },
  { id: 'haftung-inhalte', label: 'Haftung für Inhalte' },
  { id: 'haftung-links', label: 'Haftung für Links' },
  { id: 'urheberrecht', label: 'Urheberrecht' },
  { id: 'streitbeilegung', label: 'Streitbeilegung' },
];

export default function ImpressumPage() {
  // Apply SEO meta‑data – guard against unexpected runtime errors
  try {
    useSEO({
      title: 'Impressum - BR Webdesign & AI Solutions Titisee-Neustadt',
      description:
        'Impressum von BR Webdesign & AI Solutions – Benjamin Reick, Freiburger Straße 19, 79822 Titisee-Neustadt. Angaben gemäß § 5 TMG.',
      keywords:
        'Impressum, BR Webdesign, Benjamin Reick, Titisee-Neustadt, 79822',
      canonical: '/impressum',
      schema: {
        '@context': 'https://schema.org',
        '@graph': [
          generateWebPageSchema(
            'Impressum',
            'Impressum von BR Webdesign & AI Solutions gemäß § 5 TMG',
            '/impressum'
          ),
        ],
      },
    });
  } catch (e) {
    console.error('SEO error:', e);
  }

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <div className="min-h-screen bg-white font-inter">
      <PageNavbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-brand-beige text-brand-brown text-xs font-semibold tracking-wide uppercase">
              Rechtliches
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-caprasimo text-brand-black mb-4">
            Impressum
          </h1>
          <p className="text-brand-grey text-base">Angaben gemäß § 5 TMG</p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto flex gap-16">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block w-56 shrink-0">
            <div className="sticky top-28">
              <nav className="flex flex-col gap-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className="text-left text-sm text-brand-grey hover:text-brand-black hover:font-medium transition-all py-2 px-3 rounded-md hover:bg-brand-beige-light/50 cursor-pointer whitespace-nowrap"
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <article className="space-y-12">
              {/* Angaben */}
              <div id="angaben" className="scroll-mt-28">
                <h4 className="text-xl font-semibold text-brand-black mb-4">
                  <a href="#angaben" className="hover:text-brand-green transition-colors">
                    Angaben gemäß § 5 TMG
                  </a>
                </h4>
                <div className="bg-brand-beige-light/40 rounded-lg p-6 space-y-1">
                  <p className="text-brand-black font-medium text-base">Benjamin Reick</p>
                  <p className="text-brand-grey text-sm">BR Webdesign & AI Solutions</p>
                  <p className="text-brand-grey text-sm">Freiburger Straße 19</p>
                  <p className="text-brand-grey text-sm">79822 Titisee-Neustadt</p>
                  <p className="text-brand-grey text-sm">Deutschland</p>
                </div>
              </div>

              {/* Kontakt */}
              <div id="kontakt" className="scroll-mt-28">
                <h4 className="text-xl font-semibold text-brand-black mb-4">
                  <a href="#kontakt" className="hover:text-brand-green transition-colors">
                    Kontakt
                  </a>
                </h4>
                <div className="bg-brand-beige-light/40 rounded-lg p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-md bg-brand-green/10">
                      <i className="ri-phone-line text-brand-green text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-brand-grey">Telefon</p>
                      <a
                        href="tel:01623651571"
                        className="text-sm text-brand-black hover:text-brand-green transition-colors cursor-pointer"
                      >
                        0162 365 1571
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-md bg-brand-green/10">
                      <i className="ri-mail-line text-brand-green text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-brand-grey">E-Mail</p>
                      <a
                        href="mailto:info-aiagents@web.de"
                        className="text-sm text-brand-black hover:text-brand-green transition-colors cursor-pointer"
                      >
                        info-aiagents@web.de
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Verantwortlich */}
              <div id="verantwortlich" className="scroll-mt-28">
                <h4 className="text-xl font-semibold text-brand-black mb-4">
                  <a
                    href="#verantwortlich"
                    className="hover:text-brand-green transition-colors"
                  >
                    Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV
                  </a>
                </h4>
                <div className="bg-brand-beige-light/40 rounded-lg p-6 space-y-1">
                  <p className="text-brand-black font-medium text-sm">Benjamin Reick</p>
                  <p className="text-brand-grey text-sm">Freiburger Straße 19</p>
                  <p className="text-brand-grey text-sm">79822 Titisee-Neustadt</p>
                </div>
              </div>

              {/* Haftung für Inhalte */}
              <div id="haftung-inhalte" className="scroll-mt-28">
                <h4 className="text-xl font-semibold text-brand-black mb-4">
                  <a
                    href="#haftung-inhalte"
                    className="hover:text-brand-green transition-colors"
                  >
                    Haftung für Inhalte
                  </a>
                </h4>
                <p className="text-sm text-brand-grey leading-relaxed mb-3">
                  Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf
                  diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis
                  10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte
                  oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu
                  forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
                </p>
                <p className="text-sm text-brand-grey leading-relaxed">
                  Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen
                  nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche
                  Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten
                  Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden
                  Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
                </p>
              </div>

              {/* Haftung für Links */}
              <div id="haftung-links" className="scroll-mt-28">
                <h4 className="text-xl font-semibold text-brand-black mb-4">
                  <a
                    href="#haftung-links"
                    className="hover:text-brand-green transition-colors"
                  >
                    Haftung für Links
                  </a>
                </h4>
                <p className="text-sm text-brand-grey leading-relaxed mb-3">
                  Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte
                  wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch
                  keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der
                  jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
                </p>
                <p className="text-sm text-brand-grey leading-relaxed">
                  Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche
                  Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der
                  Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der
                  verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung
                  nicht zumum. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige
                  Links umgehend entfernen.
                </p>
              </div>

              {/* Urheberrecht */}
              <div id="urheberrecht" className="scroll-mt-28">
                <h4 className="text-xl font-semibold text-brand-black mb-4">
                  <a
                    href="#urheberrecht"
                    className="hover:text-brand-green transition-colors"
                  >
                    Urheberrecht
                  </a>
                </h4>
                <p className="text-sm text-brand-grey leading-relaxed mb-3">
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten
                  unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung,
                  Verbreitung und jede Art der Verwertung außerhalb der Grenzen des
                  Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors
                  bzw. Erstellers.
                </p>
                <p className="text-sm text-brand-grey leading-relaxed">
                  Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen
                  Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber
                  erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden
                  Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine
                  Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden
                  Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte
                  umgehend entfernen.
                </p>
              </div>

              {/* Streitbeilegung */}
              <div id="streitbeilegung" className="scroll-mt-28">
                <h4 className="text-xl font-semibold text-brand-black mb-4">
                  <a
                    href="#streitbeilegung"
                    className="hover:text-brand-green transition-colors"
                  >
                    Online-Streitbeilegung
                  </a>
                </h4>
                <p className="text-sm text-brand-grey leading-relaxed mb-3">
                  Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung
                  (OS) bereit: <a
                    href="https://ec.europa.eu/consumers/odr/"
                    rel="nofollow"
                    className="text-brand-green hover:text-brand-green-light underline cursor-pointer"
                  >
                    https://ec.europa.eu/consumers/odr/
                  </a>
                </p>
                <p className="text-sm text-brand-grey leading-relaxed">
                  Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
                  Verbraucherschlichtungsstelle teilzunehmen.
                </p>
              </div>
            </article>
          </main>
        </div>
      </section>

      <Footer />
      <ScrollToTopButton />
    </div>
  );
}
